/**
 * YieldVarianceReport - Standalone page for viewing all yield variance records
 * Shows sessions where actual yield deviated ±1% or more from expected yield
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useLanguage } from '../../i18n/LanguageContext';
import DatePickerDropdown from '../../components/DatePickerDropdown';
import {
  TrendingUp,
  TrendingDown,
  Download,
  Filter,
  ChevronDown,
  ChevronRight,
  Calendar,
  RefreshCw,
  AlertTriangle,
  CheckCircle,
  Printer,
  Info
} from 'lucide-react';
import HoverTooltip from './components/HoverTooltip';
import { formatYield, formatYieldVariance, isYieldFallback } from '../../hooks/useYieldFactor';
import yieldVarianceService, {
  YieldVarianceRecord,
  YieldVarianceSummary,
  YieldVarianceFilters
} from '../../services/yieldVarianceService';
import operatorService from '../../services/operatorService';
import type { FinishProductCarryover } from '../Mobile/components/WasteSummaryStep';
import YieldVarianceSuggestionsBanner from '../../components/YieldVarianceSuggestionsBanner';
import { formatMMDDYYYY, formatElapsedTime } from '../../utils/dateUtils';

// Waste type enum → i18n key (yieldVariance.wasteTypes.*). Unknown types fall back to raw enum.
const WASTE_TYPE_I18N_KEYS: Record<string, string> = {
  by_product: 'yieldVariance.wasteTypes.byProduct',
  by_product_marketable: 'yieldVariance.wasteTypes.byProductMarketable',
  by_product_production: 'yieldVariance.wasteTypes.byProductProduction',
  disposable: 'yieldVariance.wasteTypes.disposable',
  water_liquid: 'yieldVariance.wasteTypes.waterLiquid',
  human_error: 'yieldVariance.wasteTypes.humanError',
  remainder_intact: 'yieldVariance.wasteTypes.remainderIntact',
  remainder_not_intact: 'yieldVariance.wasteTypes.remainderNotIntact',
  finish_product_not_intact: 'yieldVariance.wasteTypes.finishProductNotIntact',
};

// LF standards are OUTPUT lb/hr for a 2-worker crew (WCO-2474). Kept local — the frontend can't
// import the backend @tnb/labour-factor package (mirrors AddSessionModal/SessionLeftColumn.tsx).
const STANDARD_CREW_SIZE = 2;

/**
 * ThroughputCalcTooltip (WCO-2628) — the live, per-row breakdown of how the observed Throughput
 * (lb/wh) was computed, with THIS row's numbers plugged into the two-step formula. Renders
 * "not measurable" when any input is missing (matches the "—" shown in the dropdown).
 *   Step 1: per-worker rate = output ÷ productive_hrs ÷ workers
 *   Step 2: × STANDARD_CREW_SIZE (2) → standard 2-worker-crew basis
 */
const ThroughputCalcTooltip: React.FC<{
  record: YieldVarianceRecord;
  t: (key: string) => string;
}> = ({ record, t }) => {
  const output = record.actual_output_weight;
  const workers = record.worker_count;
  const productiveSeconds = record.productive_seconds;
  const productiveHours = productiveSeconds == null ? null : productiveSeconds / 3600;

  const measurable =
    output > 0 && workers > 0 && productiveHours != null && productiveHours > 0;

  if (!measurable) {
    return (
      <div className="text-xs">
        <div className="font-semibold mb-1">{t('yieldVariance.throughputCalc.title') || "Throughput (lb/wh) — how it's calculated"}</div>
        <div className="text-gray-300">{t('yieldVariance.throughputCalc.notMeasurable') || 'Not measurable — missing output, worker count, or productive time for this run.'}</div>
      </div>
    );
  }

  // Compute EXACTLY (full precision) so the final result equals the stored/dropdown throughput.
  // The displayed intermediates are rounded for readability, so re-multiplying the shown 2-dp
  // values by hand can be off by ~0.01 — the disclaimer note below flags that.
  const perWorker = output / productiveHours! / workers;         // Step 1: per-worker-hour rate
  const throughput = perWorker * STANDARD_CREW_SIZE;             // Step 2: standard-crew basis
  const hrs4 = productiveHours!.toFixed(4);
  const hms = formatElapsedTime(productiveHours!);

  return (
    <div className="text-xs font-mono leading-relaxed">
      <div className="font-sans font-semibold mb-2">{t('yieldVariance.throughputCalc.title') || "Throughput (lb/wh) — how it's calculated"}</div>
      <div className="space-y-0.5">
        <div>{t('yieldVariance.throughputCalc.output') || 'Output'}: {output.toFixed(2)} lb</div>
        <div>{t('yieldVariance.throughputCalc.productiveTime') || 'Productive time'}: {hms} ({hrs4} {t('yieldVariance.throughputCalc.hrs') || 'hrs'})</div>
        <div>{t('yieldVariance.throughputCalc.workers') || 'Workers'}: {workers}</div>
      </div>
      <div className="mt-2 pt-2 border-t border-gray-600 space-y-0.5">
        <div className="font-sans font-medium">{t('yieldVariance.throughputCalc.step1') || 'Step 1 — per-worker rate = output ÷ hrs ÷ workers'}</div>
        <div>= {output.toFixed(2)} ÷ {hrs4} ÷ {workers}</div>
        <div>= {perWorker.toFixed(2)} {t('yieldVariance.throughputCalc.perWorkerHour') || 'lb per worker-hour'}</div>
      </div>
      <div className="mt-2 pt-2 border-t border-gray-600 space-y-0.5">
        <div className="font-sans font-medium">{t('yieldVariance.throughputCalc.step2') || 'Step 2 — × standard crew size (2)'}</div>
        <div>= {perWorker.toFixed(2)} × {STANDARD_CREW_SIZE}</div>
        <div>= {throughput.toFixed(2)} lb/wh</div>
      </div>
      <div className="mt-2 pt-2 border-t border-gray-600 font-sans text-gray-300">
        {t('yieldVariance.throughputCalc.why') || '÷workers gives an honest per-person rate; ×2 puts it on the standard 2-worker basis so a crew at standard scores LF ≈ 1.0 at any headcount.'}
      </div>
      <div className="mt-2 font-sans text-gray-400 italic">
        {t('yieldVariance.throughputCalc.roundingNote') || 'Values are rounded for display; the result is computed at full precision, so re-multiplying the shown 2-decimal numbers by hand may differ by ~0.01.'}
      </div>
    </div>
  );
};

interface YieldVarianceReportProps {
  embedded?: boolean; // When true, hides page title (used in Reports tab view)
  summaryCollapsed?: boolean; // When true, hides summary cards and suggestions banner
}

const YieldVarianceReport: React.FC<YieldVarianceReportProps> = ({ embedded = false, summaryCollapsed = false }) => {
  const { t } = useLanguage();

  // State
  const [variances, setVariances] = useState<YieldVarianceRecord[]>([]);
  const [summary, setSummary] = useState<YieldVarianceSummary>({
    total_variances: 0,
    over_yield_count: 0,
    under_yield_count: 0,
    avg_variance: 0
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  // WCO-2628: sub_session_numbers whose throughput ⓘ tooltip is currently open — each highlights
  // that row's calc inputs (Output / Productive Time / Workers) so the tooltip's math maps to live
  // values. A Set (not a single value) so several tooltips can be open and highlighted at once.
  const [throughputHighlightRows, setThroughputHighlightRows] = useState<Set<string>>(new Set());
  const setThroughputHighlight = useCallback((subSessionNumber: string, on: boolean) => {
    setThroughputHighlightRows(prev => {
      // No-op (return same ref) when already in the desired state — HoverTooltip re-fires
      // onOpenChange on every render (its callback identity changes), so a fresh Set each time
      // would loop. Only allocate when membership actually changes.
      if (on === prev.has(subSessionNumber)) return prev;
      const next = new Set(prev);
      if (on) next.add(subSessionNumber); else next.delete(subSessionNumber);
      return next;
    });
  }, []);
  // Carryover (finish_product_ni) per sub-session — lazy-loaded on row expansion.
  const [carryoverBySubSession, setCarryoverBySubSession] = useState<Record<string, FinishProductCarryover[]>>({});

  // Pagination
  const [pagination, setPagination] = useState({
    total: 0,
    limit: 50,
    offset: 0,
    has_more: false
  });

  // Filters
  const [filters, setFilters] = useState<YieldVarianceFilters>({
    startDate: '',
    endDate: '',
    productSku: '',
    operatorId: '',
    stationId: '',
    varianceType: 'all',
    limit: 50,
    offset: 0
  });

  // Filter options — loaded once on mount from a stable, filter-independent source (WCO-2612)
  const [products, setProducts] = useState<Array<{ sku: string; name: string }>>([]);
  const [operators, setOperators] = useState<Array<{ id: string; name: string }>>([]);
  const [stations, setStations] = useState<Array<{ id: string; name: string }>>([]);
  const [showFilters, setShowFilters] = useState(false);

  // Fetch data
  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await yieldVarianceService.getYieldVariances(filters);
      setVariances(response.variances);
      setSummary(response.summary);
      setPagination(response.pagination);
      // NOTE: dropdown options are NOT derived here — they come from a stable, filter-independent
      // source loaded once on mount (see the options effect below). Deriving them from the
      // filtered `response.variances` made each filter shrink its own option list (WCO-2612).
    } catch (err) {
      console.error('Error fetching yield variances:', err);
      setError(t('yieldVariance.fetchError') || 'Failed to load yield variance data');
    } finally {
      setLoading(false);
    }
  }, [filters, t]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Prune throughput highlights to rows still present. A row can be filtered/paginated away (or the
  // page unmounted) while its tooltip is open — HoverTooltip fires onOpenChange only on open-state
  // changes, not on unmount — so its id would otherwise linger in the Set and re-highlight the row
  // (and leak) when it reappears. Intersect with the current variances whenever they change.
  useEffect(() => {
    setThroughputHighlightRows(prev => {
      if (prev.size === 0) return prev;
      const present = new Set(variances.map(v => v.sub_session_number));
      const next = new Set<string>();
      for (const id of prev) if (present.has(id)) next.add(id);
      return next.size === prev.size ? prev : next;
    });
  }, [variances]);

  // Load the stable filter dropdown options ONCE on mount. These are distinct values across all
  // yield-variance-eligible rows, independent of the active filters — so applying a filter no
  // longer collapses the Product/Operator/Station lists to the current result set (WCO-2612).
  useEffect(() => {
    yieldVarianceService.getYieldVarianceFilterOptions()
      .then(options => {
        setProducts(options.products);
        setOperators(options.operators);
        setStations(options.stations);
      })
      .catch(err => console.error('Error loading yield variance filter options:', err));
  }, []);

  // Handle filter changes
  const handleFilterChange = (key: keyof YieldVarianceFilters, value: string) => {
    setFilters(prev => ({
      ...prev,
      [key]: value,
      offset: 0 // Reset pagination when filters change
    }));
  };

  // Check if any filters are active
  const hasActiveFilters = () => {
    return !!(filters.startDate || filters.endDate || filters.productSku ||
              filters.operatorId || filters.stationId ||
              (filters.varianceType && filters.varianceType !== 'all'));
  };

  // Clear all filters
  const handleClearFilters = () => {
    setFilters({
      startDate: '',
      endDate: '',
      productSku: '',
      operatorId: '',
      stationId: '',
      varianceType: 'all',
      limit: 50,
      offset: 0
    });
  };

  // Handle pagination
  const handlePageChange = (newOffset: number) => {
    setFilters(prev => ({
      ...prev,
      offset: newOffset
    }));
  };

  // Toggle row expansion (lazy-loads carryover on first expand)
  const toggleRowExpansion = (subSessionNumber: string) => {
    setExpandedRows(prev => {
      const next = new Set(prev);
      if (next.has(subSessionNumber)) {
        next.delete(subSessionNumber);
      } else {
        next.add(subSessionNumber);
        if (!(subSessionNumber in carryoverBySubSession)) {
          operatorService.getFinishProductCarryover(subSessionNumber)
            .then(result => setCarryoverBySubSession(prev => ({ ...prev, [subSessionNumber]: result.carryover || [] })))
            .catch(() => setCarryoverBySubSession(prev => ({ ...prev, [subSessionNumber]: [] })));
        }
      }
      return next;
    });
  };

  // Export to Excel — fills any missing carryover from the same endpoint
  // used by row expansion, then passes the full map to the exporter.
  const handleExport = async () => {
    const dateStr = new Date().toISOString().split('T')[0];
    const exportMap: Record<string, FinishProductCarryover[]> = {};
    await Promise.all(variances.map(async v => {
      const sn = v.sub_session_number;
      if (carryoverBySubSession[sn]) {
        exportMap[sn] = carryoverBySubSession[sn];
        return;
      }
      try {
        const r = await operatorService.getFinishProductCarryover(sn);
        exportMap[sn] = r.carryover || [];
      } catch {
        exportMap[sn] = [];
      }
    }));
    yieldVarianceService.exportToExcel(variances, `yield-variances-${dateStr}.xlsx`, exportMap);
  };

  // Print report
  const handlePrint = () => {
    if (!variances.length) return;

    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    const tableRows = variances.map(v => {
      // expected_yield / variance are null when the (product, material) pair has no configured yield
      const varianceColor = v.variance == null ? '' : v.variance >= 0 ? 'color: #16a34a;' : 'color: #dc2626;';

      // Format raw material: English (Chinese) on first line, item# on second line
      const rawMaterialLine1 = [
        v.raw_material.name,
        v.raw_material.chinese_name ? `(${v.raw_material.chinese_name})` : ''
      ].filter(Boolean).join(' ');
      const rawMaterialLine2 = v.raw_material.item_number || '';

      // Format product: English (Chinese) on first line, SKU on second line
      const productLine1 = [
        v.product_name,
        v.product_chinese_name ? `(${v.product_chinese_name})` : ''
      ].filter(Boolean).join(' ');
      const productLine2 = v.product_sku || '';

      return `
        <tr>
          <td style="border: 1px solid #ddd; padding: 8px;">${v.sub_session_number || v.session_number}</td>
          <td style="border: 1px solid #ddd; padding: 8px;">${formatMMDDYYYY(v.completed_at)}</td>
          <td style="border: 1px solid #ddd; padding: 8px;">${rawMaterialLine1 || '-'}<br><small style="color: #666;">${rawMaterialLine2}</small></td>
          <td style="border: 1px solid #ddd; padding: 8px;">${productLine1 || '-'}<br><small style="color: #666;">${productLine2}</small></td>
          <td style="border: 1px solid #ddd; padding: 8px; text-align: center;${isYieldFallback(v.expected_yield_source) ? ' color: #d97706;' : ''}">${v.expected_yield == null ? '—' : formatYield(v.expected_yield, undefined, v.expected_yield_source)}</td>
          <td style="border: 1px solid #ddd; padding: 8px; text-align: center;">${formatYield(v.actual_yield)}</td>
          <td style="border: 1px solid #ddd; padding: 8px; text-align: center; font-weight: bold; ${varianceColor}">${v.variance == null ? '—' : formatYieldVariance(v.variance)}</td>
          <td style="border: 1px solid #ddd; padding: 8px;">${v.operator.name || v.operator.employee_number || '-'}</td>
          <td style="border: 1px solid #ddd; padding: 8px;">${v.station.name || '-'}</td>
          <td style="border: 1px solid #ddd; padding: 8px;">${v.approval?.supervisor_name || '-'}</td>
          <td style="border: 1px solid #ddd; padding: 8px;">${v.approval?.reason || '-'}</td>
        </tr>
      `;
    }).join('');

    // Build filters display
    const filterParts: string[] = [];
    if (filters.startDate) filterParts.push(`<strong>From:</strong> ${filters.startDate}`);
    if (filters.endDate) filterParts.push(`<strong>To:</strong> ${filters.endDate}`);
    if (filters.varianceType && filters.varianceType !== 'all') {
      filterParts.push(`<strong>Type:</strong> ${filters.varianceType === 'over' ? 'Over Yield' : 'Under Yield'}`);
    }
    const filtersHtml = filterParts.length > 0
      ? `<div class="filters">${filterParts.join(' &nbsp;|&nbsp; ')}</div>`
      : '';

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>Yield Variance Report - ${new Date().toLocaleDateString()}</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 20px; }
          h1 { font-size: 18px; margin-bottom: 10px; }
          .filters { margin-bottom: 10px; font-size: 12px; color: #666; }
          .summary { margin-bottom: 15px; font-size: 14px; display: flex; gap: 30px; }
          .summary-item { padding: 8px 16px; background: #f3f4f6; border-radius: 4px; }
          .summary-item.over { color: #16a34a; }
          .summary-item.under { color: #dc2626; }
          table { border-collapse: collapse; width: 100%; font-size: 11px; }
          th { background-color: #e5e7eb; color: #111; padding: 10px 8px; text-align: left; border: 1px solid #d1d5db; font-weight: 600; }
          td { border: 1px solid #ddd; padding: 8px; }
          tr:nth-child(even) { background-color: #f9fafb; }
          @media print {
            body { margin: 0; padding: 10px; }
            h1 { font-size: 14px; }
          }
        </style>
      </head>
      <body>
        <h1>Yield Variance Report - ${new Date().toLocaleDateString()}</h1>
        ${filtersHtml}
        <div class="summary">
          <div class="summary-item"><strong>Total:</strong> ${summary.total_variances}</div>
          <div class="summary-item over"><strong>Over Yield:</strong> ${summary.over_yield_count}</div>
          <div class="summary-item under"><strong>Under Yield:</strong> ${summary.under_yield_count}</div>
          <div class="summary-item"><strong>Avg Variance:</strong> ${summary.avg_variance >= 0 ? '+' : ''}${summary.avg_variance.toFixed(2)}%</div>
        </div>
        <table>
          <thead>
            <tr>
              <th>Session</th>
              <th>Date</th>
              <th>Raw Material</th>
              <th>Product</th>
              <th style="text-align: center;">Expected</th>
              <th style="text-align: center;">Actual</th>
              <th style="text-align: center;">Variance</th>
              <th>Operator</th>
              <th>Station</th>
              <th>Approved By</th>
              <th>Reason</th>
            </tr>
          </thead>
          <tbody>
            ${tableRows}
          </tbody>
        </table>
      </body>
      </html>
    `);

    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
    }, 250);
  };

  // Format variance for display with color (variance is decimal, e.g. 0.023)
  const formatVariance = (variance: number | null) => {
    // null when the (product, material) pair has no configured yield — show a neutral gap marker
    if (variance == null) return <span className="text-gray-400 dark:text-gray-500">—</span>;
    const color = variance >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400';
    return (
      <span className={`font-medium ${color}`}>
        {formatYieldVariance(variance)}
      </span>
    );
  };

  // Format a labour factor value as 2-decimal number; null/undefined → "—"
  const formatLF = (value: number | null) =>
    value === null || value === undefined ? '—' : value.toFixed(2);

  // Format LF variance as a signed percentage. Backend already sends
  // expected − actual, so positive = improvement (lower LF = less effort) = green,
  // matching yield variance's convention.
  const formatLFVariance = (variance: number | null) => {
    if (variance === null || variance === undefined) return <span className="text-gray-400">—</span>;
    const color = variance >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400';
    const sign = variance > 0 ? '+' : '';
    return <span className={`font-medium ${color}`}>{sign}{(variance * 100).toFixed(2)}%</span>;
  };

  // Calculate total pages
  const totalPages = Math.ceil(pagination.total / pagination.limit);
  const currentPage = Math.floor(pagination.offset / pagination.limit) + 1;

  return (
    <div className={embedded ? 'space-y-6' : 'p-6 max-w-[1800px] mx-auto'}>
      {/* Header - only shown when not embedded */}
      {!embedded && (
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            {t('yieldVariance.title') || 'Yield Variance Report'}
          </h1>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            {t('yieldVariance.description') || 'Sessions with ±1% or more yield variance from expected'}
          </p>
        </div>
      )}

      {/* Summary Cards — collapse when tab is re-clicked */}
      {!summaryCollapsed && <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-3 sm:p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {t('yieldVariance.totalVariances') || 'Total Variances'}
              </p>
              <p className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white mt-1">
                {summary.total_variances}
              </p>
            </div>
            <div className="p-2 sm:p-3 bg-gray-100 dark:bg-gray-700 rounded-lg">
              <AlertTriangle className="w-5 h-5 sm:w-6 sm:h-6 text-gray-600 dark:text-gray-300" />
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-3 sm:p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {t('yieldVariance.overYield') || 'Over Yield'}
              </p>
              <p className="text-xl sm:text-2xl font-bold text-green-600 dark:text-green-400 mt-1">
                {summary.over_yield_count}
              </p>
            </div>
            <div className="p-2 sm:p-3 bg-green-100 dark:bg-green-900/30 rounded-lg">
              <TrendingUp className="w-5 h-5 sm:w-6 sm:h-6 text-green-600 dark:text-green-400" />
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-3 sm:p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {t('yieldVariance.underYield') || 'Under Yield'}
              </p>
              <p className="text-xl sm:text-2xl font-bold text-red-600 dark:text-red-400 mt-1">
                {summary.under_yield_count}
              </p>
            </div>
            <div className="p-2 sm:p-3 bg-red-100 dark:bg-red-900/30 rounded-lg">
              <TrendingDown className="w-5 h-5 sm:w-6 sm:h-6 text-red-600 dark:text-red-400" />
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-3 sm:p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {t('yieldVariance.avgVariance') || 'Avg Variance'}
              </p>
              <p className={`text-xl sm:text-2xl font-bold mt-1 ${summary.avg_variance >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                {formatYieldVariance(summary.avg_variance)}
              </p>
            </div>
            <div className="p-2 sm:p-3 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
              <CheckCircle className="w-5 h-5 sm:w-6 sm:h-6 text-blue-600 dark:text-blue-400" />
            </div>
          </div>
        </div>
      </div>}

      {/* Recurring Variance Suggestions Banner — also collapses with summary */}
      {!summaryCollapsed && <div className="mb-6">
        <YieldVarianceSuggestionsBanner />
      </div>}

      {/* Filters Header */}
      <div className="flex items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
          >
            <Filter className="w-4 h-4 text-gray-500" />
            <span className="hidden sm:inline">{t('common.filters') || 'Filters'}</span>
          </button>
          {hasActiveFilters() && (
            <button
              onClick={handleClearFilters}
              className="inline-flex items-center gap-1 px-2 py-2 text-sm font-medium text-gray-500 dark:text-gray-400 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 hover:text-gray-700 dark:hover:text-gray-200"
            >
              <span className="text-gray-400">×</span>
              <span className="hidden sm:inline">{t('common.clear') || 'Clear'}</span>
            </button>
          )}
          <button
            onClick={fetchData}
            disabled={loading}
            className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
            title={t('common.refresh') || 'Refresh'}
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
        <div className="flex gap-2">
          <button
            onClick={handlePrint}
            disabled={variances.length === 0}
            className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-white bg-gray-600 rounded-lg hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Printer className="w-4 h-4 sm:mr-2" />
            <span className="hidden sm:inline">{t('common.print') || 'Print'}</span>
          </button>
          <button
            onClick={handleExport}
            disabled={variances.length === 0}
            className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Download className="w-4 h-4 sm:mr-2" />
            <span className="hidden sm:inline">{t('common.export') || 'Export'}</span>
          </button>
        </div>
      </div>

      {/* Filters Panel - collapsible */}
      {showFilters && (
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-4 mb-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-4">
          {/* Date Range */}
          <div>
            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
              {t('yieldVariance.startDate') || 'Start Date'}
            </label>
            <DatePickerDropdown
              selected={filters.startDate || null}
              onSelect={(date) => handleFilterChange('startDate', date)}
            >
              {({ onClick }) => (
                <button type="button" onClick={onClick} className="w-full flex items-center justify-between pl-9 pr-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white relative">
                  <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                  {filters.startDate ? (() => { const d = new Date(filters.startDate + 'T00:00:00'); return `${d.toLocaleDateString('en-US', { weekday: 'short' })} — ${d.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' })}`; })() : 'Select date...'}
                </button>
              )}
            </DatePickerDropdown>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
              {t('yieldVariance.endDate') || 'End Date'}
            </label>
            <DatePickerDropdown
              selected={filters.endDate || null}
              onSelect={(date) => handleFilterChange('endDate', date)}
            >
              {({ onClick }) => (
                <button type="button" onClick={onClick} className="w-full flex items-center justify-between pl-9 pr-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white relative">
                  <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                  {filters.endDate ? (() => { const d = new Date(filters.endDate + 'T00:00:00'); return `${d.toLocaleDateString('en-US', { weekday: 'short' })} — ${d.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' })}`; })() : 'Select date...'}
                </button>
              )}
            </DatePickerDropdown>
          </div>

          {/* Product Filter */}
          <div>
            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
              {t('yieldVariance.product') || 'Product'}
            </label>
            <select
              value={filters.productSku || ''}
              onChange={(e) => handleFilterChange('productSku', e.target.value)}
              className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            >
              <option value="">{t('common.all') || 'All'}</option>
              {products.map(p => (
                <option key={p.sku} value={p.sku}>{p.name || p.sku}</option>
              ))}
            </select>
          </div>

          {/* Operator Filter */}
          <div>
            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
              {t('yieldVariance.operator') || 'Operator'}
            </label>
            <select
              value={filters.operatorId || ''}
              onChange={(e) => handleFilterChange('operatorId', e.target.value)}
              className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            >
              <option value="">{t('common.all') || 'All'}</option>
              {operators.map(o => (
                <option key={o.id} value={o.id}>{o.name || o.id}</option>
              ))}
            </select>
          </div>

          {/* Station Filter */}
          <div>
            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
              {t('yieldVariance.station') || 'Station'}
            </label>
            <select
              value={filters.stationId || ''}
              onChange={(e) => handleFilterChange('stationId', e.target.value)}
              className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            >
              <option value="">{t('common.all') || 'All'}</option>
              {stations.map(s => (
                <option key={s.id} value={s.id}>{s.name || `Station ${s.id}`}</option>
              ))}
            </select>
          </div>

          {/* Variance Type Filter */}
          <div>
            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
              {t('yieldVariance.varianceType') || 'Variance Type'}
            </label>
            <select
              value={filters.varianceType || 'all'}
              onChange={(e) => handleFilterChange('varianceType', e.target.value)}
              className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            >
              <option value="all">{t('yieldVariance.allVariances') || 'All Variances'}</option>
              <option value="over">{t('yieldVariance.overYieldOnly') || 'Over Yield Only'}</option>
              <option value="under">{t('yieldVariance.underYieldOnly') || 'Under Yield Only'}</option>
            </select>
          </div>
        </div>
      </div>
      )}

      {/* Error State */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-6">
          <p className="text-red-700 dark:text-red-300">{error}</p>
        </div>
      )}

      {/* Data Table */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-max min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-100 dark:bg-gray-700">
              <tr>
                <th className="w-10 px-3 py-3"></th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap">
                  {t('yieldVariance.session') || 'Session'}
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap">
                  {t('yieldVariance.date') || 'Date'}
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap">
                  {t('yieldVariance.rawMaterial') || 'Raw Material'}
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap">
                  {t('yieldVariance.product') || 'Product'}
                </th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap">
                  {t('yieldVariance.expectedYield') || 'Expected'}
                </th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap">
                  {t('yieldVariance.actualYield') || 'Actual'}
                </th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap">
                  {t('yieldVariance.variance') || 'Variance'}
                </th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap">
                  {t('yieldVariance.lfExpected') || 'LF Expected'}
                </th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap">
                  {t('yieldVariance.lfActual') || 'LF Actual'}
                </th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap">
                  {t('yieldVariance.lfVariance') || 'LF Variance'}
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap">
                  {t('yieldVariance.operator') || 'Operator'}
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap">
                  {t('yieldVariance.station') || 'Station'}
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap">
                  {t('yieldVariance.approvedBy') || 'Approved By'}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {loading ? (
                <tr>
                  <td colSpan={14} className="px-4 py-8 text-center">
                    <RefreshCw className="w-6 h-6 animate-spin mx-auto text-gray-400" />
                    <p className="mt-2 text-sm text-gray-500">{t('common.loading') || 'Loading...'}</p>
                  </td>
                </tr>
              ) : variances.length === 0 ? (
                <tr>
                  <td colSpan={14} className="px-4 py-8 text-center">
                    <p className="text-gray-500 dark:text-gray-400">
                      {t('yieldVariance.noData') || 'No yield variances found for the selected filters'}
                    </p>
                  </td>
                </tr>
              ) : (
                variances.map((v) => {
                  // WCO-2628: rows the throughput ⓘ tooltip is built from get an amber highlight
                  // while that tooltip is open, so the calc maps visibly onto the live values.
                  const isThroughputInput = throughputHighlightRows.has(v.sub_session_number);
                  const inputHl = isThroughputInput
                    ? 'bg-amber-100/70 dark:bg-amber-900/30 -mx-1 px-1 rounded transition-colors'
                    : 'transition-colors';
                  return (
                  <React.Fragment key={v.sub_session_number}>
                    {/* Main Row */}
                    <tr
                      className="hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer"
                      onClick={() => toggleRowExpansion(v.sub_session_number)}
                    >
                      <td className="px-3 py-3">
                        {expandedRows.has(v.sub_session_number) ? (
                          <ChevronDown className="w-4 h-4 text-gray-400" />
                        ) : (
                          <ChevronRight className="w-4 h-4 text-gray-400" />
                        )}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900 dark:text-white">
                          {v.sub_session_number || v.session_number}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300 whitespace-nowrap">
                        {formatMMDDYYYY(v.completed_at)}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="text-sm text-gray-900 dark:text-white">{v.raw_material.name || '-'}</div>
                        {v.raw_material.chinese_name && (
                          <div className="text-sm text-gray-500 dark:text-gray-400">{v.raw_material.chinese_name}</div>
                        )}
                        <div className="text-xs text-gray-500 dark:text-gray-400">{v.raw_material.item_number || '-'}</div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="text-sm text-gray-900 dark:text-white">{v.product_name}</div>
                        {v.product_chinese_name && (
                          <div className="text-sm text-gray-500 dark:text-gray-400">{v.product_chinese_name}</div>
                        )}
                        <div className="text-xs text-gray-500 dark:text-gray-400">{v.product_sku}</div>
                      </td>
                      <td className={`px-4 py-3 text-center text-sm whitespace-nowrap ${isYieldFallback(v.expected_yield_source) ? 'text-amber-600 dark:text-amber-400' : 'text-gray-600 dark:text-gray-300'}`}>
                        {v.expected_yield == null ? '—' : formatYield(v.expected_yield, undefined, v.expected_yield_source)}
                      </td>
                      <td className="px-4 py-3 text-center text-sm text-gray-600 dark:text-gray-300 whitespace-nowrap">
                        {formatYield(v.actual_yield)}
                      </td>
                      <td className="px-4 py-3 text-center whitespace-nowrap">
                        {formatVariance(v.variance)}
                      </td>
                      <td className="px-4 py-3 text-center text-sm text-gray-600 dark:text-gray-300 whitespace-nowrap">
                        {formatLF(v.expected_labour_factor)}
                      </td>
                      <td className="px-4 py-3 text-center text-sm text-gray-600 dark:text-gray-300 whitespace-nowrap">
                        <span className="inline-flex items-center justify-center gap-1.5">
                          {formatLF(v.actual_labour_factor)}
                          {v.needs_review && (
                            <span
                              className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-400"
                              title={t('yieldVariance.anomalyTooltip') || 'Actual labour factor deviates ±20% from standard'}
                            >
                              <AlertTriangle className="w-3 h-3" />
                              {t('yieldVariance.needsReview') || 'Needs review'}
                            </span>
                          )}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center whitespace-nowrap">
                        {formatLFVariance(v.labour_factor_variance)}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300 whitespace-nowrap">
                        {v.operator.name || v.operator.employee_number || '-'}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300 whitespace-nowrap">
                        {v.station.name || '-'}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300 whitespace-nowrap">
                        {v.approval?.supervisor_name || '-'}
                      </td>
                    </tr>

                    {/* Expanded Details Row */}
                    {expandedRows.has(v.sub_session_number) && (
                      <tr className="bg-gray-50 dark:bg-gray-900/30">
                        <td colSpan={14} className="px-6 py-4">
                          {/* WCO-3107: the table is `w-max` (sizes to its 14 columns) inside an
                              overflow-x-auto scroller, so this colSpan cell can be much wider than the
                              visible area and the detail's 3rd column used to sit off-screen. `sticky
                              left-0` pins the block to the visible left edge on horizontal scroll.
                              `w-fit` alone is measured against the wide `w-max` table (NOT the visible
                              scroller), so we also cap the block to the visible content width so the
                              responsive grid always wraps to fit; `w-fit` still keeps it from stretching
                              wider than its (capped) columns when there's room.

                              Copilot follow-up (#765 review): a SINGLE `100vw-19rem` cap is wrong on both
                              sides of `md` because the shell it subtracts is itself responsive:
                                • below `md` the desktop sidebar is `hidden md:flex` (not in layout at all),
                                  so subtracting 16rem of sidebar over-deducts and crushes the stacked block
                                  (~71px at 375px); and
                                • at `md`+ the real deduction is larger than 19rem, so the grid can still
                                  extend past the visible cell.
                              So the cap is now BREAKPOINT-SPECIFIC, mirroring the actual shell. The main
                              container padding is itself responsive — `px-4 sm:px-6 md:px-8` (Layout.tsx) —
                              so there are THREE tiers, not two (Copilot #767 review caught the missing `sm`):
                                • base (< sm, no sidebar): main `px-4` 2rem + page `p-6` 3rem + cell `px-6`
                                  3rem = 8rem  → `max-w-[calc(100vw-8rem)]`
                                • `sm`–`md` (no sidebar yet, main padding grows to `sm:px-6` 3rem):
                                  0 + 3 + 3 + 3 = 9rem → `sm:max-w-[calc(100vw-9rem)]`
                                • `md`+ (16rem sidebar appears, main padding grows to `md:px-8` 4rem):
                                  16 + 4 + 3 + 3 = 26rem → `md:max-w-[calc(100vw-26rem)]`
                              Each is conservative (never clip) and never collapses the stacked content;
                              a collapsed sidebar only leaves MORE room, so the cap stays safe. */}
                          <div className="sticky left-0 w-fit max-w-[calc(100vw-8rem)] sm:max-w-[calc(100vw-9rem)] md:max-w-[calc(100vw-26rem)] grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-x-8 gap-y-4">
                            {/* Weight Details */}
                            <div className="max-w-xs">
                              <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                {t('yieldVariance.weightDetails') || 'Weight Details'}
                              </h4>
                              <div className="space-y-1 text-sm">
                                <div className="flex justify-between">
                                  <span className="text-gray-500">{t('yieldVariance.targetWeight') || 'Target Weight'}:</span>
                                  <span className="text-gray-900 dark:text-white">{v.target_output_weight.toFixed(2)} lb</span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-gray-500">{t('yieldVariance.inputWeight') || 'Input Weight'}:</span>
                                  <span className="text-gray-900 dark:text-white">{v.initial_material_weight.toFixed(2)} lb</span>
                                </div>
                                <div className={`flex justify-between ${inputHl}`}>
                                  <span className="text-gray-500">{t('yieldVariance.outputWeight') || 'Output Weight'}:</span>
                                  <span className="text-gray-900 dark:text-white">{v.actual_output_weight.toFixed(2)} lb</span>
                                </div>
                                {(() => {
                                  const carryover = (carryoverBySubSession[v.sub_session_number] || [])
                                    .reduce((sum, c) => sum + (Number(c.allocated_weight) || 0), 0);
                                  if (carryover <= 0) return null;
                                  return (
                                    <div className="flex justify-between pl-4">
                                      <span className="text-xs text-gray-500">↳ {t('yieldVariance.carryover') || 'Carryover'}:</span>
                                      <span className="text-xs text-gray-600 dark:text-gray-400">{carryover.toFixed(2)} lb</span>
                                    </div>
                                  );
                                })()}
                              </div>
                            </div>

                            {/* Waste Breakdown */}
                            <div className="max-w-xs">
                              <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                {t('yieldVariance.wasteBreakdown') || 'Waste Breakdown'}
                              </h4>
                              {v.waste_breakdown && v.waste_breakdown.length > 0 ? (
                                <div className="space-y-1 text-sm">
                                  {v.waste_breakdown.map((w, idx) => (
                                    <div key={idx} className="flex justify-between">
                                      <span className="text-gray-500">
                                        {(WASTE_TYPE_I18N_KEYS[w.waste_type]
                                          ? t(WASTE_TYPE_I18N_KEYS[w.waste_type])
                                          : w.waste_type)}:
                                      </span>
                                      <span className="text-gray-900 dark:text-white">{w.waste_weight.toFixed(2)} lb</span>
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                <p className="text-sm text-gray-500">{t('yieldVariance.noWasteData') || 'No waste data recorded'}</p>
                              )}
                            </div>

                            {/* Throughput & Labour (WCO-2627: throughput + its inputs) + Approval Details below */}
                            <div className="max-w-xs">
                              <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                {t('yieldVariance.throughputLabour') || 'Throughput & Labour'}
                              </h4>
                              <div className="space-y-1 text-sm">
                                <div className="flex justify-between">
                                  {/* WCO-2628: (i) tooltip renders the live two-step throughput calc for this row */}
                                  <span className="text-gray-500 flex items-center gap-1">
                                    {t('yieldVariance.observedThroughput') || 'Throughput (lb/wh)'}:
                                    <HoverTooltip
                                      tapToOpen
                                      onOpenChange={(o) => setThroughputHighlight(v.sub_session_number, o)}
                                      className="inline-flex cursor-help text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                                      panelClassName="z-50 w-80 max-w-[90vw] p-3 rounded-lg shadow-xl bg-gray-800 text-white border border-gray-700"
                                      content={<ThroughputCalcTooltip record={v} t={t} />}
                                    >
                                      <Info className="w-3.5 h-3.5" />
                                    </HoverTooltip>
                                  </span>
                                  <span className="text-gray-900 dark:text-white">
                                    {v.observed_throughput == null ? '—' : v.observed_throughput.toFixed(2)}
                                  </span>
                                </div>
                                <div className={`flex justify-between ${inputHl}`}>
                                  <span className="text-gray-500">{t('yieldVariance.productiveTime') || 'Productive Time'}:</span>
                                  <span className="text-gray-900 dark:text-white">
                                    {v.productive_seconds == null
                                      ? '—'
                                      : `${formatElapsedTime(v.productive_seconds / 3600)} (${(v.productive_seconds / 3600).toFixed(4)} hrs)`}
                                  </span>
                                </div>
                                <div className={`flex justify-between ${inputHl}`}>
                                  <span className="text-gray-500">{t('yieldVariance.workers') || 'Workers'}:</span>
                                  <span className="text-gray-900 dark:text-white">{v.worker_count}</span>
                                </div>
                              </div>

                              {/* Approval Details — stacked below the throughput block in the same column */}
                              <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mt-4 mb-2">
                                {t('yieldVariance.approvalDetails') || 'Approval Details'}
                              </h4>
                              {v.approval ? (
                                <div className="space-y-1 text-sm">
                                  <div className="flex justify-between">
                                    <span className="text-gray-500">{t('yieldVariance.approvedBy') || 'Approved By'}:</span>
                                    <span className="text-gray-900 dark:text-white">{v.approval.supervisor_name}</span>
                                  </div>
                                  {v.approval.approved_at && (
                                    <div className="flex justify-between">
                                      <span className="text-gray-500">{t('yieldVariance.approvedAt') || 'Approved At'}:</span>
                                      <span className="text-gray-900 dark:text-white">{formatMMDDYYYY(v.approval.approved_at)}</span>
                                    </div>
                                  )}
                                  {v.approval.reason && (
                                    <div className="mt-2">
                                      <span className="text-gray-500 block">{t('yieldVariance.reason') || 'Reason'}:</span>
                                      <span className="text-gray-900 dark:text-white">{v.approval.reason}</span>
                                    </div>
                                  )}
                                </div>
                              ) : (
                                <p className="text-sm text-gray-500">
                                  {v.completion_type === 'override'
                                    ? t('yieldVariance.overrideNoDetails') || 'Override completion (no approval details)'
                                    : t('yieldVariance.noApprovalRequired') || 'No approval required'}
                                </p>
                              )}
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {!loading && variances.length > 0 && (
          <div className="px-4 py-3 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between">
            <div className="text-sm text-gray-500 dark:text-gray-400">
              {t('common.showing') || 'Showing'} {pagination.offset + 1} - {Math.min(pagination.offset + variances.length, pagination.total)} {t('common.of') || 'of'} {pagination.total}
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => handlePageChange(Math.max(0, pagination.offset - pagination.limit))}
                disabled={pagination.offset === 0}
                className="px-3 py-1 text-sm text-gray-700 dark:text-gray-200 border border-gray-300 dark:border-gray-600 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                {t('common.previous') || 'Previous'}
              </button>
              <span className="px-3 py-1 text-sm text-gray-600 dark:text-gray-300">
                {currentPage} / {totalPages}
              </span>
              <button
                onClick={() => handlePageChange(pagination.offset + pagination.limit)}
                disabled={!pagination.has_more}
                className="px-3 py-1 text-sm text-gray-700 dark:text-gray-200 border border-gray-300 dark:border-gray-600 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                {t('common.next') || 'Next'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default YieldVarianceReport;
